import { useEffect, useRef, useState } from "react";
import type { Idea, Message, Project } from "../lib/types";
import { generateId } from "../lib/types";
import { IdeaCard } from "./IdeaCard";
import { NewIdeaSheet } from "./NewIdeaSheet";

interface Props {
  ideas: Idea[];
  messages: Map<string, Message[]>;
  projects: Project[];
  userId: string;
  selectedIdeaId: string | null;
  onSelectIdea: (id: string) => void;
  onDeleteIdea: (id: string) => void;
  onCreateIdea: (title: string, brainDump: string, projectId: string | null) => void;
  onCreateProject: (project: Project) => void;
}

const ALL = "__all__";
const INBOX = "__inbox__";

export function IdeaList({
  ideas,
  messages,
  projects,
  userId,
  selectedIdeaId,
  onSelectIdea,
  onDeleteIdea,
  onCreateIdea,
  onCreateProject,
}: Props) {
  const [activeProject, setActiveProject] = useState<string>(ALL);
  const [showNewSheet, setShowNewSheet] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const visibleIdeas = ideas.filter((i) => {
    if (i.deletedAt !== null) return false;
    if (activeProject === INBOX && i.projectId !== null) return false;
    if (activeProject !== ALL && activeProject !== INBOX && i.projectId !== activeProject) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return i.title.toLowerCase().includes(q) || i.brainDump.toLowerCase().includes(q);
    }
    return true;
  });

  // Keyboard ↑/↓ to navigate visible ideas
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      const t = e.target as HTMLElement;
      if (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT") return;
      e.preventDefault();
      const idx = visibleIdeas.findIndex((i) => i.id === selectedIdeaId);
      const next = e.key === "ArrowDown"
        ? Math.min(idx + 1, visibleIdeas.length - 1)
        : Math.max(idx - 1, 0);
      if (visibleIdeas[next]) onSelectIdea(visibleIdeas[next].id);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [visibleIdeas, selectedIdeaId, onSelectIdea]);

  // Scroll selected card into view when navigated via keyboard
  useEffect(() => {
    if (!selectedIdeaId || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idea-id="${selectedIdeaId}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedIdeaId]);

  function ideaCount(pid: string): number {
    if (pid === ALL) return ideas.filter((i) => i.deletedAt === null).length;
    if (pid === INBOX) return ideas.filter((i) => i.deletedAt === null && i.projectId === null).length;
    return ideas.filter((i) => i.deletedAt === null && i.projectId === pid).length;
  }

  const currentProjectId = activeProject === ALL || activeProject === INBOX ? null : activeProject;

  return (
    <div className="flex flex-col h-full">
      {/* Project switcher */}
      <div className="flex gap-2 overflow-x-auto py-3 px-4 scrollbar-none shrink-0">
        {[
          { id: ALL, label: "All" },
          { id: INBOX, label: "Inbox" },
          ...projects.map((p) => ({ id: p.id, label: p.name })),
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveProject(tab.id)}
            className={`shrink-0 text-xs font-mono px-3 py-1.5 rounded-full transition-colors ${
              activeProject === tab.id
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {tab.label}
            <span className="ml-1 opacity-60">{ideaCount(tab.id)}</span>
          </button>
        ))}
        <button
          onClick={() => setShowNewProject(true)}
          className="shrink-0 text-xs font-mono px-3 py-1.5 rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-gray-500 hover:text-gray-600 transition-colors"
        >
          + Project
        </button>
      </div>

      {/* Search */}
      <div className="px-4 pb-2 shrink-0">
        <input
          ref={searchRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search ideas…"
          className="w-full text-xs font-mono border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-gray-800 bg-white"
        />
      </div>

      {/* Idea list */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 pb-4">
        {visibleIdeas.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm font-mono">
            {searchQuery ? "No matching ideas." : "No ideas yet. Tap + to add one."}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {visibleIdeas.map((idea) => (
              <div key={idea.id} data-idea-id={idea.id}>
                <IdeaCard
                  idea={idea}
                  messages={messages.get(idea.id) ?? []}
                  project={projects.find((p) => p.id === idea.projectId)}
                  isSelected={idea.id === selectedIdeaId}
                  onSelect={() => onSelectIdea(idea.id)}
                  onDelete={() => onDeleteIdea(idea.id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New idea button */}
      <div className="px-4 py-3 shrink-0">
        <button
          onClick={() => setShowNewSheet(true)}
          className="w-full bg-gray-900 text-white font-mono text-sm font-bold py-2.5 rounded-xl shadow-sm hover:bg-gray-700 transition-colors"
        >
          + New Idea
        </button>
      </div>

      {/* New idea sheet */}
      {showNewSheet && (
        <div className="fixed inset-0 z-40 bg-black/30">
          <NewIdeaSheet
            projects={projects}
            defaultProjectId={currentProjectId}
            onSave={onCreateIdea}
            onClose={() => setShowNewSheet(false)}
          />
        </div>
      )}

      {/* New project inline */}
      {showNewProject && (
        <div className="fixed inset-0 z-40 bg-black/30 flex items-end justify-center">
          <div
            className="bg-white rounded-t-2xl p-5 pb-8 max-w-[480px] w-full mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
            <h2 className="font-syne text-lg font-bold mb-3">New project</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!newProjectName.trim()) return;
                const now = Date.now();
                onCreateProject({
                  id: generateId(),
                  userId,
                  name: newProjectName.trim(),
                  repos: [],
                  createdAt: now,
                  updatedAt: now,
                  deletedAt: null,
                });
                setShowNewProject(false);
                setNewProjectName("");
              }}
              className="flex gap-2"
            >
              <input
                autoFocus
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Project name"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-gray-800"
              />
              <button
                type="submit"
                className="bg-gray-900 text-white rounded-lg px-4 text-sm font-mono font-bold"
              >
                Create
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
